import { ThemeEvents } from '@theme/events';

class EstimatedCartPrice extends HTMLElement {
  constructor() {
    super();
    this.countryAddresses = {
      PL: { countryCode: 'PL', zip: '00-001', city: 'Warsaw', address1: 'Marszalkowska 1' },
      DE: { countryCode: 'DE', zip: '10115', city: 'Berlin', address1: 'Unter den Linden 1' },
      FR: { countryCode: 'FR', zip: '75001', city: 'Paris', address1: '1 Rue de Rivoli' },
      GB: { countryCode: 'GB', zip: 'SW1A 1AA', city: 'London', address1: '10 Downing Street' },
      US: { countryCode: 'US', zip: '10001', provinceCode: 'NY', city: 'New York', address1: '350 5th Ave' },
      IT: { countryCode: 'IT', zip: '00100', city: 'Rome', address1: 'Via del Corso 1' },
      ES: { countryCode: 'ES', zip: '28001', city: 'Madrid', address1: 'Calle de Alcala 1' },
      NL: { countryCode: 'NL', zip: '1012 JS', city: 'Amsterdam', address1: 'Dam 1' },
      BE: { countryCode: 'BE', zip: '1000', city: 'Brussels', address1: 'Rue de la Loi 1' },
      AT: { countryCode: 'AT', zip: '1010', city: 'Vienna', address1: 'Stephansplatz 1' },
      CZ: { countryCode: 'CZ', zip: '110 00', city: 'Prague', address1: 'Vaclavske namesti 1' },
      SK: { countryCode: 'SK', zip: '811 01', city: 'Bratislava', address1: 'Hlavne namestie 1' },
    };
  }

  connectedCallback() {
    this.onCartUpdate = () => this.fetchCheapestShipping();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.onCartUpdate);

    this.fetchCheapestShipping();
  }

  disconnectedCallback() {
    if (this.onCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
      document.removeEventListener(ThemeEvents.discountUpdate, this.onCartUpdate);
    }
  }

  get aiGenId() {
    return this.dataset.aiGenId || 'blockestimatedcartprice';
  }

  get hiddenClass() {
    return `ai-hidden-${this.aiGenId}`;
  }

  get loadingClass() {
    return `ai-cheapest-shipping-loading-${this.aiGenId}`;
  }

  get spinnerClass() {
    return `ai-cheapest-shipping-spinner-${this.aiGenId}`;
  }

  get contentClass() {
    return `ai-cheapest-shipping-content-${this.aiGenId}`;
  }

  get detailsClass() {
    return `ai-cheapest-shipping-details-${this.aiGenId}`;
  }

  get labelClass() {
    return `cart-totals__total-label cart-primary-typography ai-cheapest-shipping-label-${this.aiGenId}`;
  }

  get nameClass() {
    return `cart-totals__total-value ai-cheapest-shipping-name-${this.aiGenId}`;
  }

  get priceClass() {
    return `cart-totals__total-value ai-cheapest-shipping-price-${this.aiGenId}`;
  }

  get apiEndpoint() {
    return `/api/${this.dataset.apiVersion || '2026-04'}/graphql.json`;
  }

  get storefrontAccessToken() {
    return this.dataset.storefrontAccessToken || '';
  }

  get label() {
    return this.dataset.label || '';
  }

  get loadingText() {
    return this.dataset.loadingText || '';
  }

  detectCountry() {
    if (window.Shopify && Shopify.country) {
      return Shopify.country.toUpperCase();
    }

    if (window.Shopify && Shopify.locale) {
      const localeCountry = Shopify.locale.split('-').pop().toUpperCase();
      if (this.countryAddresses[localeCountry]) return localeCountry;
    }

    return (this.dataset.defaultCountry || 'PL').toUpperCase();
  }

  getPlaceholderAddress(countryCode) {
    return this.countryAddresses[countryCode] || this.countryAddresses.PL;
  }

  buildDeliveryAddress(address) {
    const deliveryAddress = {
      address1: address.address1,
      city: address.city,
      countryCode: address.countryCode,
      zip: address.zip,
    };

    if (address.provinceCode) {
      deliveryAddress.provinceCode = address.provinceCode;
    }

    return deliveryAddress;
  }

  async graphql(query, variables) {
    const response = await fetch(this.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch Storefront API data');
    }

    return response.json();
  }

  extractDeliveryOptions(cart) {
    return (cart?.deliveryGroups?.nodes || []).flatMap((group) => group.deliveryOptions || []);
  }

  async fetchDeliveryOptions(cartId) {
    const query = `
      query CartDeliveryOptions($id: ID!) {
        cart(id: $id) {
          id
          deliveryGroups(first: 10) {
            nodes {
              id
              deliveryOptions {
                handle
                title
                estimatedCost {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `;

    const data = await this.graphql(query, { id: cartId });

    if (data.errors || !data.data?.cart) {
      return [];
    }

    return this.extractDeliveryOptions(data.data.cart);
  }

  /**
   * Storefront API domyślnie pomija stawki przewoźników (Octolize: paczkomaty, punkty DPD),
   * więc bez tego zapytania koszyk widział tylko opcje kurierskie i „najtańsza dostawa"
   * była zawyżona. `withCarrierRates: true` wymaga @defer, a wtedy odpowiedź przychodzi
   * jako multipart/mixed zamiast zwykłego JSON-a — stąd własne parsowanie niżej.
   */
  async fetchCarrierDeliveryOptions(cartId) {
    const query = `
      query CartCarrierDeliveryOptions($id: ID!) {
        cart(id: $id) {
          id
          ... on Cart @defer {
            deliveryGroups(first: 10, withCarrierRates: true) {
              nodes {
                id
                deliveryOptions {
                  handle
                  title
                  estimatedCost {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(this.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'multipart/mixed; deferSpec=20220824, application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
      },
      body: JSON.stringify({ query, variables: { id: cartId } }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch carrier delivery options');
    }

    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      return this.collectDeliveryOptions(await response.json());
    }

    const boundaryMatch = contentType.match(/boundary="?([^";]+)"?/);
    const boundary = `--${boundaryMatch ? boundaryMatch[1] : 'graphql'}`;
    const options = [];

    for (const part of (await response.text()).split(boundary)) {
      const start = part.indexOf('{');
      const end = part.lastIndexOf('}');
      if (start === -1 || end <= start) continue;
      try {
        options.push(...this.collectDeliveryOptions(JSON.parse(part.slice(start, end + 1))));
      } catch (error) {
        // fragment multiparta bez poprawnego JSON-a (nagłówki, separator końcowy)
      }
    }

    const unique = new Map();
    for (const option of options) {
      if (!unique.has(option.handle)) unique.set(option.handle, option);
    }
    return [...unique.values()];
  }

  /** Zbiera deliveryOptions z dowolnego kształtu odpowiedzi (data / incremental przy @defer). */
  collectDeliveryOptions(payload) {
    const found = [];
    const walk = (node) => {
      if (!node || typeof node !== 'object') return;
      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }
      if (Array.isArray(node.deliveryOptions)) {
        found.push(...node.deliveryOptions.filter((option) => option && option.estimatedCost));
      }
      Object.values(node).forEach(walk);
    };
    walk(payload);
    return found;
  }

  async fetchCheapestShipping() {
    try {
      if (!this.storefrontAccessToken) {
        throw new Error('Missing Storefront API access token');
      }

      this.renderLoadingState();

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (!cart.items || cart.items.length === 0) {
        this.hide();
        return;
      }

      const country = this.detectCountry();
      const address = this.getPlaceholderAddress(country);
      const input = {
        lines: cart.items.map((item) => ({
          merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity: item.quantity,
        })),
        buyerIdentity: {
          countryCode: address.countryCode,
        },
        delivery: {
          addresses: [
            {
              selected: true,
              oneTimeUse: true,
              address: {
                deliveryAddress: this.buildDeliveryAddress(address),
              },
            },
          ],
        },
      };

      const mutation = `
        mutation CartCreateWithDelivery($input: CartInput!) {
          cartCreate(input: $input) {
            cart {
              id
              deliveryGroups(first: 10) {
                nodes {
                  id
                  deliveryOptions {
                    handle
                    title
                    estimatedCost {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const data = await this.graphql(mutation, { input });
      const cartCreate = data.data?.cartCreate;

      if (data.errors || !cartCreate || cartCreate.userErrors.length > 0) {
        throw new Error('Cart creation failed');
      }

      // Najpierw pełna lista ze stawkami przewoźników (paczkomaty itd.). Stawki liczą się
      // chwilę po utworzeniu koszyka, stąd ponawianie z odstępem.
      let deliveryOptions = [];

      for (let attempt = 0; attempt < 4 && deliveryOptions.length === 0; attempt += 1) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 600 * attempt));
        }
        deliveryOptions = await this.fetchCarrierDeliveryOptions(cartCreate.cart.id).catch(() => []);
      }

      // Degradacja do starego zachowania: opcje bez stawek przewoźników.
      if (deliveryOptions.length === 0) {
        deliveryOptions = this.extractDeliveryOptions(cartCreate.cart);
      }

      for (let attempt = 0; attempt < 3 && deliveryOptions.length === 0; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
        deliveryOptions = await this.fetchDeliveryOptions(cartCreate.cart.id);
      }

      if (deliveryOptions.length === 0) {
        this.hide();
        return;
      }

      const cheapest = deliveryOptions.reduce((min, option) => {
        const price = parseFloat(option.estimatedCost.amount);
        const minPrice = parseFloat(min.estimatedCost.amount);
        return price < minPrice ? option : min;
      });

      this.displayShippingRate(cheapest);
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
      this.hide();
    }
  }

  renderLoadingState() {
    this.innerHTML = `
      <div class="${this.loadingClass}">
        <div class="${this.spinnerClass}"></div>
        <span>${this.loadingText}</span>
      </div>
    `;
    this.classList.remove(this.hiddenClass);
  }

  displayShippingRate(rate) {
    const price = parseFloat(rate.estimatedCost.amount);
    const formattedAmount = new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
    const formattedPrice = `${formattedAmount} ${rate.estimatedCost.currencyCode}`;

    const content = document.createElement('div');
    content.className = this.contentClass;

    const label = document.createElement('span');
    label.className = this.labelClass;
    label.textContent = this.label;

    const details = document.createElement('div');
    details.className = this.detailsClass;

    const name = document.createElement('span');
    name.className = this.nameClass;
    name.textContent = rate.title;

    const priceElement = document.createElement('span');
    priceElement.className = this.priceClass;
    priceElement.textContent = formattedPrice;

    details.append(name, priceElement);
    content.append(label, details);
    this.replaceChildren(content);
    this.classList.remove(this.hiddenClass);
  }

  hide() {
    this.classList.add(this.hiddenClass);
    this.replaceChildren();
  }
}

if (!customElements.get('estimated-cart-price')) {
  customElements.define('estimated-cart-price', EstimatedCartPrice);
}
